import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import {
  trendsPosts,
  trendsCategories,
  trendsHero,
} from "@/data/trends";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const Trends = () => {
  useSeo({
    title: "Trends & News — India Sourcing Insights | Asia Sourcing",
    description:
      "Insights from India's manufacturing and sourcing industry — home décor trends, lighting trends, sourcing regulations, freight, sustainability. Asia Sourcing India.",
    path: "/trends",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          "@id": `${SITE_URL}/trends#blog`,
          name: "Asia Sourcing India Trends & News",
          url: `${SITE_URL}/trends`,
          description:
            "Insights from India's manufacturing and sourcing industry.",
          publisher: { "@id": `${SITE_URL}#organization` },
          blogPost: trendsPosts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE_URL}/trends/${p.id}`,
            datePublished: p.datePublished,
            dateModified: p.dateModified,
            author: { "@type": "Organization", name: "Asia Sourcing" },
          })),
        },
        breadcrumb([
          { name: "Home", item: `${SITE_URL}/` },
          { name: "Trends & News", item: `${SITE_URL}/trends` },
        ]),
      ],
    },
  });

  const byCategory = trendsCategories.map((c) => ({
    ...c,
    posts: trendsPosts.filter((p) => p.category === c.id),
  }));

  return (
    <div data-testid="trends-page">
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 3rem" }}
      >
        <Reveal>
          <p className="eyebrow" data-testid="trends-eyebrow">{trendsHero.eyebrow}</p>
        </Reveal>
        <WordReveal
          as="h1"
          text={trendsHero.h1}
          className="display tp-title"
          stagger={60}
          testId="trends-h1"
        />
        <Reveal as="p" delay={180} style={{ maxWidth: "68ch", color: "var(--ink-soft)", fontSize: "1.12rem", marginTop: "1.4rem", lineHeight: 1.7 }}>
          {trendsHero.intro}
        </Reveal>
        {trendsHero.paragraphs.map((para, i) => (
          <Reveal key={i} as="p" delay={220 + i * 40} style={{ maxWidth: "68ch", color: "var(--ink-soft)", fontSize: "1.02rem", marginTop: "1rem", lineHeight: 1.7 }}>
            {para}
          </Reveal>
        ))}
      </header>

      {/* Featured (latest) */}
      {trendsPosts[0] && (
        <section
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
          data-testid="trends-featured"
        >
          <Reveal as="p" className="eyebrow">★ Latest featured</Reveal>
          <Reveal>
            <Link to={`/trends/${trendsPosts[0].id}`} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "2rem", alignItems: "center", textDecoration: "none", color: "inherit", marginTop: "1rem" }}>
              {trendsPosts[0].image && (
                <img
                  src={trendsPosts[0].image}
                  alt={trendsPosts[0].imageAlt || trendsPosts[0].title}
                  width="1200"
                  height="675"
                  loading="eager"
                  style={{ width: "100%", height: "auto", maxHeight: "420px", objectFit: "cover", border: "1px solid var(--line)" }}
                />
              )}
              <div>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                  {trendsPosts[0].categoryLabel} · {trendsPosts[0].readTime}
                </p>
                <h2 className="display" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", margin: ".8rem 0", letterSpacing: "-.01em", lineHeight: 1.15 }}>
                  {trendsPosts[0].title}
                </h2>
                <p style={{ color: "var(--ink-soft)", fontSize: "1.05rem", lineHeight: 1.65, margin: 0 }}>
                  {trendsPosts[0].excerpt}
                </p>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".7rem", letterSpacing: ".14em", textTransform: "uppercase", marginTop: "1.4rem" }}>
                  Read the article →
                </p>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      {/* By category */}
      {byCategory.map((cat) =>
        cat.posts.length === 0 ? null : (
          <section
            key={cat.id}
            className="wrap"
            style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
            data-testid={`trends-cat-${cat.id}`}
          >
            <Reveal as="p" className="eyebrow">{cat.label}</Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.6rem", marginTop: "2rem" }}>
              {cat.posts.map((p) => (
                <Reveal key={p.id}>
                  <Link
                    to={`/trends/${p.id}`}
                    style={{ display: "block", border: "1px solid var(--line)", textDecoration: "none", color: "inherit", height: "100%" }}
                    data-testid={`trends-tile-${p.id}`}
                  >
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.imageAlt || p.title}
                        width="800"
                        height="450"
                        loading="lazy"
                        style={{ display: "block", width: "100%", height: "220px", objectFit: "cover", borderBottom: "1px solid var(--line)" }}
                      />
                    )}
                    <div style={{ padding: "1.4rem 1.4rem 1.6rem" }}>
                      <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                        {p.categoryLabel} · {p.readTime}
                      </p>
                      <p style={{ fontFamily: "var(--display)", fontSize: "1.2rem", margin: ".55rem 0 .6rem", letterSpacing: "-.005em", lineHeight: 1.3, minHeight: "3em" }}>
                        {p.title}
                      </p>
                      <p style={{ color: "var(--ink-soft)", fontSize: ".95rem", margin: 0, lineHeight: 1.6 }}>
                        {p.excerpt}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </section>
        )
      )}

      {/* CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow">Ready to source?</Reveal>
        <Reveal as="h2" className="display">
          Turn insight into a sourcing brief.
        </Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="trends-cta-contact">
            Start an enquiry
          </Link>
          <Link to="/products" className="btn btn-lg" data-testid="trends-cta-products">
            Explore product catalogue
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Trends;
